import { gql, useMutation, useQuery } from "@apollo/client";
import {
  Avatar,
  Button,
  Icon,
  IndexTable,
  Layout,
  LegacyCard,
  Text,
  Modal as Modals,
  Checkbox,
} from "@shopify/polaris";
import { TickMinor } from "@shopify/polaris-icons";
import moment from "moment";
import React, { useCallback, useContext, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { Image } from "src/components/shopify/Image";
import { PageLayout } from "src/components/shopify/PageLayout";
import { PolarisEmptyState } from "src/components/shopify/PolarisEmptyState";
import { ContextUser } from "src/context/ContextUser";
import {
  DisbursementItem,
  SupplierDisbursementAvailableListDocument,
  SupplierInternalDisbursementListDocument,
  useSupplierQuery,
  useUpdateMerchantInfoMutation,
} from "src/graphql/codegen_graphql";
import { Modal } from "src/hooks/modal";
import { Toast } from "src/hooks/toast";
import { DropZoneOwnerNumber } from "src/screens/account/components/DropZoneOwnerNumber";
import { ModalExplanationNote } from "./components/ModalExplanationNote";
import { ModalLogSellerBankAccount } from "./components/ModalLogSellerBankAccount";
import { PricesToolTips } from "../products/item/upload/PricesToolTips";

const REQUEST_DISBURSEMENT = gql`
  mutation disbursementRequest($password: String) {
    disbursementRequest(password: $password)
  }
`;

const tabs = [
  {
    id: "available",
    content: "Available",
    accessibilityLabel: "All customers",
    panelID: "all-customers-content-1",
  },
  {
    id: "PENDING",
    content: "Pending",
    panelID: "accepts-marketing-content-1",
  },
  {
    id: "VERIFIED",
    content: "Verified",
    panelID: "repeat-customers-content-1",
  },
  {
    id: "TRANSFERED",
    content: "Transferred",
    panelID: "prospects-content-1",
  },
  {
    id: "TRANSIT",
    content: "In Transit",
    panelID: "prospects-content-1",
  },
  {
    id: "COMPLETED",
    content: "Paid",
    panelID: "prospects-content-1",
  },
];

const agreement = {
  en: "I acknowledge and agree that my bank account information below is correct, and the company shall disburse this amount to the bank account below. I understand that any losses incurred due to incorrect bank account details provided by me are my sole responsibility, and the company is not liable for such losses.",
  km: "ខ្ញុំទទួលខុសត្រូវនិងយល់ព្រមថាព័ត៌មានគណនីធនាគារខាងក្រោមពិតជាត្រឹមត្រូវ ក្រុមហ៊ុនត្រូវបញ្ជូនប្រាក់មកគណនីធនាគារខាងក្រោមសម្រាប់ប្រតិបត្តិការមួយនេះ។ ក្នុងករណីបាត់បង់ណាមួយដែលបណ្តាលមកពីព័ត៌មានគណនីធនាគារដែលខ្ញុំផ្តល់ ខ្ញុំទទួលខុសត្រូវហើយក្រុមហ៊ុនមិនត្រូវទទួលខុសត្រូវជាដាច់ខាត។",
};

function RequestDisbursementButton({ onCompleted }: { onCompleted: any }) {
  const { t } = useTranslation();
  const { merchant } = useContext(ContextUser);
  const [password, setPassword] = useState("");
  const [agree, setAgree] = useState(false);

  const { data } = useSupplierQuery({
    variables: {
      supplierId: 535,
    },
  });

  const [disbursementRequest] = useMutation(REQUEST_DISBURSEMENT, {
    refetchQueries: ["supplierDisbursementList", "merchantTotalHoldBalance"],
    onCompleted: (res) => {
      if (isNaN(Number(res.disbursementRequest))) {
        Toast.onShow(res.disbursementRequest, "info", "right");
      } else {
        onCompleted();
      }
      window.location.reload();
    },
    onError: (err) => {
      Toast.onShow(err.message, "danger", "right");
    },
  });

  const onSave = () => {
    disbursementRequest({
      variables: {
        password,
      },
    });
  };

  return (
    <div className="p-3">
      <label htmlFor="">{t("password")}</label>
      <input
        value={password}
        onChange={(e) => setPassword(e.target.value)}
        type="password"
        className="lf-form-control form-control-sm"
        autoFocus
        placeholder={t("enter_your_password")}
      />
      <br />
      <button className="btn btn-link p-0 pt-2 pb-2">{t("forget_password_disbursement")}</button>
      <br />
      <div className="mt-2 mb-2 text-sm">
        {merchant.supplier?.paymentGatewayID ? (
          <div style={{ display: "flex", alignItems: "flex-start", gap: "0.25rem" }}>
            <div>
              <Checkbox checked={agree} onChange={setAgree} label labelHidden id="agree" />
            </div>
            <label htmlFor="agree">
              <div>
                <p>
                  I acknowledge and agree to allow the following Cambodia-based bank account of my representative or
                  agent to receive this disbursement on my behalf. I agree that the company is not responsible for any
                  of my loss once the company finishes the disbursement transfer to this Cambodia-based bank account. I
                  acknowledge and agree that the following Cambodia-based bank account of my representative or agent
                  below is correct, and the company shall disburse this amount to this bank account below. I understand
                  that any losses incurred due to incorrect bank account details provided by me are my sole
                  responsibility, and the company is not liable for such losses.
                </p>
                <p>
                  <b>My Representative or Agent’s Bank Information</b>
                  <div>
                    Bank Name: <b>{data?.supplier?.bankName || ""}</b>
                  </div>
                  <div>
                    Account Number: <b>{data?.supplier?.bankAccountNumber || ""}</b>
                  </div>
                  <div>
                    Account Holder's Name: <b>{data?.supplier?.bankAccountHolderName || ""}</b>
                  </div>
                </p>
                <p>
                  I agree to allow the representative or agent to transfer the disbursement to the following bank
                  account by any means at any exchange rate not lower than 90% of the rate (ABA Buys) published by ABA
                  in Cambodia.
                </p>
                <div className="mt-2">
                  <b>My Bank Information</b>
                  <div>
                    Bank Name: <b>{merchant.bankName}</b>
                  </div>
                  <div>
                    Account Number: <b>{merchant.bankAccountNumber}</b>
                  </div>
                  <div>
                    Account Hold Name: <b>{merchant.bankAccountHoldName}</b>
                  </div>
                </div>
              </div>
            </label>
          </div>
        ) : (
          <Checkbox
            label={
              <div>
                <div>{merchant.origin === "Local" ? agreement.km : agreement.en}</div>
                <div className="mt-2">
                  <div>
                    Bank Name: <b>{merchant.bankName}</b>
                  </div>
                  <div>
                    Account Number: <b>{merchant.bankAccountNumber}</b>
                  </div>
                  <div>
                    Account Hold Name: <b>{merchant.bankAccountHoldName}</b>
                  </div>
                </div>
              </div>
            }
            checked={agree}
            onChange={setAgree}
          />
        )}
      </div>
      <div className="text-right">
        <button className="btn btn-primary btn-sm" onClick={onSave} disabled={!agree}>
          {t("request")}
        </button>
      </div>
    </div>
  );
}

function UpdateOwnerNumber({ active, setActive }: { active: boolean; setActive: any }) {
  const { merchant } = useContext(ContextUser);
  const [ownerNumberInput, setOwnerNumberInput] = useState(merchant.ownerNumber + "");
  const [updateMerchantInfo] = useUpdateMerchantInfoMutation({
    refetchQueries: ["merchant"],
  });

  const toggleActive = useCallback(() => setActive(!active), [active, setActive]);

  const handleOnSave = useCallback(() => {
    const data = {
      logo: merchant.logo,
      banner: merchant.banner,
      address: merchant.address,
      coordLat: String(merchant.coordLat),
      coordLng: String(merchant.coordLng),
      contact: merchant.contact,
      emails: merchant.emails,
      vat: merchant.vat,
      fiscalCodeNumber: merchant.fiscalCodeNumber,
      bankAccountHoldName: merchant.bankAccountHoldName,
      bankAccountNumber: merchant.bankAccountNumber,
      bankName: merchant.bankName,
      ownerName: merchant.ownerName,
      ownerType: merchant.ownerType,
      ownerNumber: ownerNumberInput,
      frontStoreImage: merchant.frontStoreImage,
      abaLink: merchant.abaLink,
      verifiedBankAccount: merchant.verifiedBankAccount
        ? isNaN(Number(merchant.verifiedBankAccount))
          ? merchant.verifiedBankAccount
          : moment(new Date(Number(merchant.verifiedBankAccount || 0))).format("YYYY-MM-DD")
        : undefined,
    };

    updateMerchantInfo({
      variables: {
        data,
      },
    }).then((res) => {
      if (res.data?.updateMerchantInfo) {
        Toast.onShow(
          `Update profile was success. please click button "Request Disbursement" again.`,
          "success",
          "right",
        );
        setActive(false);
      }
    });
  }, [merchant, ownerNumberInput, setActive, updateMerchantInfo]);

  return (
    <Modals
      title="Owner Number"
      open={active}
      onClose={() => {
        toggleActive();
        setOwnerNumberInput("");
      }}
      primaryAction={{ content: "Save", onAction: handleOnSave }}
    >
      <Modals.Section>
        <div>
          <small className="text-danger">Please upload your ID card picture!</small>
          <DropZoneOwnerNumber value={ownerNumberInput} setValue={setOwnerNumberInput} />
        </div>
      </Modals.Section>
    </Modals>
  );
}

export function RequestDisbursementScreen() {
  const { t } = useTranslation();
  const { merchant } = useContext(ContextUser);
  const [active, setActive] = useState(false);
  const [selectedTab, setSelectedTab] = useState(0);
  const [openLogSeller, setOpenLogSeller] = useState(false);
  const { data, loading } = useQuery(
    selectedTab === 0 ? SupplierDisbursementAvailableListDocument : SupplierInternalDisbursementListDocument,
    {
      variables:
        selectedTab === 0
          ? {}
          : {
              status: selectedTab === 0 ? null : tabs[selectedTab].id.toUpperCase(),
            },
    },
  );

  // calulate fee
  const totalFee = useMemo(
    () =>
      selectedTab === 0 && data
        ? (data.supplierDisbursementAvailableList as any[]).reduce((accumulator: any, item: any) => {
            const cm = item?.commission || 0;
            const hf = item?.handlingFee || 0;
            const df = item?.deliveryFee || 0;
            const amount = item?.type === "FEE" ? item?.amount || 0 : 0;
            return accumulator + cm + hf + df + amount;
          }, 0)
        : 0,
    [data, selectedTab],
  );

  // calulate total prices
  const sumTotalPrice = useMemo(
    () =>
      selectedTab === 0 && data
        ? (data.supplierDisbursementAvailableList as any[]).reduce((accumulator: any, item: any) => {
            return accumulator + Number(item?.price);
          }, 0)
        : 0,
    [data, selectedTab],
  );

  const netSales = sumTotalPrice - totalFee;

  const s = ["Pending", "Verified", "Transferring", "Paid-In Transit", "Paid"];

  return (
    <PageLayout
      pageProp={{
        title: t("page_disbursement.title"),
      }}
    >
      <Layout>
        <Layout.Section>
          {active && <UpdateOwnerNumber active={active} setActive={setActive} />}
          <LegacyCard>
            <LegacyCard.Section flush>
              <div style={{ display: "flex" }} className="p-2">
                {tabs.map((tab, index) => {
                  return (
                    <div className="mr-2" key={tab.id}>
                      <button
                        style={{
                          background: selectedTab === index ? "#e0f8ee" : "#fff",
                          color: selectedTab === index ? "#007a5c" : "#616a75",
                          padding: "0 12px",
                          borderRadius: "0.25rem",
                          height: 32,
                          fontSize: 12,
                          // fontWeight: "bold",
                        }}
                        onClick={() => setSelectedTab(index)}
                      >
                        {t(`page_disbursement.status.${tab.content.toLowerCase().replace(" ", "_")}`)}
                      </button>
                    </div>
                  );
                })}
              </div>
            </LegacyCard.Section>
            <LegacyCard.Section flush>
              <div className="p-2" style={{ display: "flex", justifyContent: "space-between" }}>
                {/* <Button url="#" size="micro" icon={<Icon source={QuestionMarkInverseMinor as any} color="base" />}>
                  Help
                </Button> */}
                <div></div>
                {selectedTab === 0 && ["SELLER", "STORE"].includes(merchant.role + "") && (
                  <Button
                    onClick={() => {
                      const validImageTypes = /(\.jpg|\.jpeg|\.png|\.gif)$/i;
                      const ownerNumber = merchant.ownerNumber || "";

                      if (!validImageTypes.exec(ownerNumber)) {
                        setActive(true);
                      } else {
                        Modal.dialog({
                          title: t("confirmation"),
                          body: <RequestDisbursementButton onCompleted={() => {}} />,
                        });
                      }
                    }}
                    variant="primary"
                    size="micro"
                    icon={<Icon source={TickMinor as any} tone="base" />}
                  >
                    {t("page_disbursement.request")}
                  </Button>
                )}
                {selectedTab > 0 && <ModalExplanationNote status={s[selectedTab - 1] as any} />}
              </div>
            </LegacyCard.Section>
            <LegacyCard.Section flush>
              <ModalLogSellerBankAccount open={openLogSeller} setOpen={setOpenLogSeller} />
              <IndexTable
                headings={
                  selectedTab === 0
                    ? ([
                        { title: "" },
                        { title: "" },
                        {
                          title: (
                            <PricesToolTips messageId={2}>
                              {" "}
                              <span>
                                Sold Price (SOP)
                                <b> (?)</b>{" "}
                              </span>{" "}
                            </PricesToolTips>
                          ),
                        },
                        {
                          title: (
                            <div
                              onClick={() => {
                                Modal.dialog({
                                  flush: true,
                                  title: t("vat_explanation"),
                                  body: (
                                    <>
                                      <Modals.Section>
                                        <strong style={{ textTransform: "uppercase" }}>
                                          {t("value")}-{t("added")} {t("tax")} ({t("")} 10%)
                                        </strong>
                                      </Modals.Section>
                                      <Modals.Section>
                                        <p>
                                          This amount includes VAT (10%). Total fee is ${totalFee.toFixed(2)} ($
                                          {totalFee.toFixed(2)}/1.1) is the actual fee{" "}
                                          {Number(totalFee / 1.1).toFixed(2)} and $
                                          {(totalFee - totalFee / 1.1).toFixed(2)} is VAT that will be paid to General
                                          Department of Taxation on your behalf.
                                        </p>
                                      </Modals.Section>
                                      <Modals.Section>
                                        <p>
                                          តម្លៃនេះបានបូកបញ្ចូលពន្ធលើតម្លៃបន្ថែម (VAT) តម្លៃសរុប ${totalFee.toFixed(2)}{" "}
                                          (${totalFee.toFixed(2)}/1.1) ថ្លៃសេវាពិតប្រាកដគឺត្រឹមតែ $
                                          {Number(totalFee / 1.1).toFixed(2)} ហើយ $
                                          {(totalFee - totalFee / 1.1).toFixed(2)}
                                          ជាពន្ធលើតម្លៃបន្ថែមដែលត្រូវបង់ទៅអគ្គនាយកដ្ឋានពន្ធដារ។
                                        </p>
                                      </Modals.Section>
                                      <Modals.Section>
                                        <p>
                                          該金額包含增值稅 (10%)。 總費用為 ${totalFee.toFixed(2)}（$
                                          {totalFee.toFixed(2)}/1.1 ），實際費用為 {Number(totalFee / 1.1).toFixed(2)}，
                                          {(totalFee - totalFee / 1.1).toFixed(2)}{" "}
                                          美元是將代表您支付給稅務總局的增值稅。
                                        </p>
                                      </Modals.Section>
                                      <Modals.Section>
                                        <p style={{ textTransform: "capitalize" }}>
                                          {t("total")}: ${totalFee.toFixed(2)}
                                        </p>
                                        <p style={{ textTransform: "capitalize" }}>
                                          {t("actual_fee")}: {t("total")}/1.1 = ${totalFee.toFixed(2)}
                                          /1.1 = ${Number(totalFee / 1.1).toFixed(2)}
                                        </p>
                                        <p style={{ textTransform: "capitalize" }}>
                                          {t("vat")}: {t("total")} - {t("actual_fee")} = ${totalFee.toFixed(2)} - $
                                          {(totalFee / 1.1).toFixed(2)} = ${(totalFee - totalFee / 1.1).toFixed(2)}
                                        </p>
                                      </Modals.Section>
                                    </>
                                  ),
                                });
                              }}
                              style={{ cursor: "pointer", color: "#047BFB" }}
                            >
                              <PricesToolTips messageId={3}>
                                {t("general.fee")}
                                <b> (?)</b>
                              </PricesToolTips>
                            </div>
                          ),
                          alignment: "start",
                        },
                        { title: t("general.net_sales"), alignment: "end" },
                      ] as any)
                    : [
                        { title: t("general.date") + "/" + t("general.id") },
                        { title: t("general.proof") },
                        {
                          title: (
                            <div
                              onClick={() => {
                                setOpenLogSeller(true);
                              }}
                              style={{ cursor: "pointer", color: "#047BFB" }}
                            >
                              {t("general.payment_info")}
                            </div>
                          ),
                        },
                        { title: t("general.amount"), alignment: "end" },
                      ]
                }
                itemCount={
                  data
                    ? selectedTab === 0
                      ? data.supplierDisbursementAvailableList.length
                      : data.supplierInternalDisbursementList.data.length
                    : 0
                }
                selectable={false}
                loading={loading}
                emptyState={<PolarisEmptyState />}
              >
                {data && selectedTab === 0 && (
                  <IndexTable.Row id="-1" position={-1} tone="success">
                    <IndexTable.Cell>
                      <div style={{ width: 100 }}>{t("general.total")}:</div>
                    </IndexTable.Cell>
                    <IndexTable.Cell></IndexTable.Cell>
                    <IndexTable.Cell>
                      <Text as="p" variant="headingSm">
                        ${Number(sumTotalPrice).toFixed(2)}
                      </Text>
                    </IndexTable.Cell>
                    <IndexTable.Cell>
                      <div className="text-start">
                        <Text as="p" variant="headingSm" tone="critical">
                          -$
                          {Number(totalFee).toFixed(2)}
                        </Text>
                      </div>
                    </IndexTable.Cell>
                    <IndexTable.Cell>
                      <div style={{ textAlign: "end" }}>
                        <Text as="p" variant="headingSm" tone="success">
                          ${Number(netSales).toFixed(2)}
                        </Text>
                      </div>
                    </IndexTable.Cell>
                  </IndexTable.Row>
                )}
                {data && selectedTab > 0 && (
                  <IndexTable.Row id="-1" position={-1} tone="success">
                    <IndexTable.Cell>{t("general.total")}:</IndexTable.Cell>
                    <IndexTable.Cell></IndexTable.Cell>
                    <IndexTable.Cell></IndexTable.Cell>
                    <IndexTable.Cell>
                      <div style={{ textAlign: "end" }}>
                        <Text as="p" variant="headingSm" tone="success">
                          $
                          {Number(
                            (data.supplierInternalDisbursementList.data as any[]).reduce(
                              (a, b) => (a = a + Number(b.amount)),
                              0,
                            ),
                          ).toFixed(2)}
                        </Text>
                      </div>
                    </IndexTable.Cell>
                  </IndexTable.Row>
                )}
                {data &&
                  (selectedTab === 0
                    ? data.supplierDisbursementAvailableList
                    : data.supplierInternalDisbursementList.data
                  )
                    .filter((_: any, i: number) => i < 100)
                    .map((item: any, index: number) => {
                      if (selectedTab > 0) {
                        return (
                          <IndexTable.Row key={index} id={index + ""} position={index}>
                            <IndexTable.Cell>
                              <small>{item.requestedDate}</small>
                              <br />
                              <small>
                                <Link to={`/applications/disbursement/detail/${item.uuid}?id=${item.id}`}>
                                  {item.uuid}
                                </Link>
                              </small>
                            </IndexTable.Cell>
                            <IndexTable.Cell>
                              {((item as DisbursementItem).proof || (item as DisbursementItem).gatewayProof) && (
                                <Image
                                  size="medium"
                                  source={
                                    (item as DisbursementItem).gatewayProof
                                      ? (item as DisbursementItem).gatewayProof + ""
                                      : (item as DisbursementItem).proof + ""
                                  }
                                  alt=""
                                />
                              )}
                            </IndexTable.Cell>
                            <IndexTable.Cell>
                              <small>
                                {t("vat")}: {item.supplier.vat}
                              </small>
                              <br />
                              <small>
                                {t("bank")}: {item.supplier.bankName}
                              </small>
                              <br />
                              <small>
                                {t("account_name")}: {item.supplier.bankAccountHolderName}
                              </small>
                              <br />
                              <small>
                                {t("account_number")}: {item.supplier.bankAccountNumber}
                              </small>
                              <br />
                              <small>
                                {t("contact")}: {item.supplier.contact}
                              </small>
                            </IndexTable.Cell>
                            <IndexTable.Cell>
                              <div style={{ textAlign: "end" }}>
                                <Link to={`/applications/disbursement/detail/${item.uuid}?id=${item.id}`}>
                                  <small>${item.amount}</small>
                                </Link>
                              </div>
                            </IndexTable.Cell>
                          </IndexTable.Row>
                        );
                      }

                      if (item.type === "FEE") {
                        const des = String(item.description).split("#");
                        return (
                          <IndexTable.Row key={index} id={index + ""} position={index} tone="success">
                            <IndexTable.Cell></IndexTable.Cell>
                            <IndexTable.Cell>
                              <div style={{ display: "flex", alignItems: "center" }}>
                                <Avatar
                                  source={
                                    ["PRODUCT_REACH", "FRONT_BANNER", "PRODUCT_BANNER"].includes(item.description) ||
                                    des.length > 1
                                      ? "/fbl_fee.svg"
                                      : "/inventory_fee.svg"
                                  }
                                  initials={item.description.charAt(0)}
                                  size="md"
                                />
                                <div className="ml-2">
                                  <small>{item.description}</small>
                                </div>
                              </div>
                            </IndexTable.Cell>
                            <IndexTable.Cell></IndexTable.Cell>
                            <IndexTable.Cell>
                              <small className="text-danger">${Number(item.amount).toFixed(2)}</small>
                            </IndexTable.Cell>
                            <IndexTable.Cell>
                              <div style={{ textAlign: "end" }} className="text-danger">
                                ${Number(item.amount).toFixed(2)}
                              </div>
                            </IndexTable.Cell>
                          </IndexTable.Row>
                        );
                      }

                      return (
                        <IndexTable.Row key={index} id={index + ""} position={index}>
                          <IndexTable.Cell>
                            <div style={{ width: 100 }}>
                              <Link target="_blank" to={`/order/v2/px/${item.orderUUID}`}>
                                <small>{item.orderUUID}</small>
                              </Link>
                            </div>
                          </IndexTable.Cell>
                          <IndexTable.Cell>
                            <div style={{ display: "flex", alignItems: "center" }}>
                              <div>
                                <Image source={item.item.thumbnail} alt="" size="medium" />
                              </div>
                              <div className="ml-2">
                                <small>{item.item.supplierCode}</small>
                                <br />
                                <small>L192: {item.item.code}</small>
                                <br />
                                <small className="text-muted">
                                  {item.color} | {item.size}
                                </small>
                              </div>
                            </div>
                          </IndexTable.Cell>
                          <IndexTable.Cell>${item.price.toFixed(2)}</IndexTable.Cell>
                          <IndexTable.Cell>
                            <div style={{ display: "flex", alignItems: "center", justifyContent: "start" }}>
                              <div
                                className="text-muted pl-2 pr-2 mr-1"
                                style={{
                                  borderLeft: "0.0625rem solid rgba(221,224,228,1)",
                                  borderRight: "0.0625rem solid rgba(221,224,228,1)",
                                  borderTopWidth: 0,
                                  borderBottomWidth: 0,
                                }}
                              >
                                <small>DF: ${item.deliveryFee.toFixed(2)}</small>
                                <br />
                                <small>HF: ${item.handlingFee.toFixed(2)}</small>
                                <br />
                                <small>CM: ${item.commission.toFixed(2)}</small>
                              </div>
                              <div>
                                <small>
                                  ={" "}
                                  <span className="text-danger">
                                    ${(item.deliveryFee + item.handlingFee + item.commission).toFixed(2)}
                                  </span>
                                </small>
                              </div>
                            </div>
                          </IndexTable.Cell>
                          <IndexTable.Cell>
                            <div style={{ textAlign: "end" }}>${item.amount.toFixed(2)}</div>
                          </IndexTable.Cell>
                        </IndexTable.Row>
                      );
                    })}
              </IndexTable>
            </LegacyCard.Section>
          </LegacyCard>
        </Layout.Section>
      </Layout>
    </PageLayout>
  );
}
